import { ActivityProgress, ActivityType, MinimalActivity } from './activity';
import { Communication } from './course-communication';
import { User } from './my-account';
import { PointsConfiguration } from './points-configuration';

export enum LectureContentQualityStatus {
  NOT_EVALUATED = 'not_evaluated',
  SUFFICIENT = 'sufficient',
  INSUFFICIENT = 'insufficient',
  CRITICALLY_INSUFFICIENT = 'critically_insufficient',
}

export enum RolePlayInteractionMode {
  TEXT = 'text',
  VOICE = 'voice',
}

export type CourseRoleplayApproval = {
  enabled: boolean,
  textEnabled: boolean,
  voiceEnabled: boolean,
};

export interface MinimalAiRolePlay extends MinimalActivity {
  type: ActivityType.AI_ROLE_PLAY;
}
type ObjectionType = { id: number; text: string };

type AiCharacterFace = {
  source: 'preset' | 'custom';
  presetKey?: string | null;
  displayUrl?: string;
  customFaces?: {
    id: number;
    uniqueId: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    displayUrl: string;
    selected: boolean;
  }[];
};

export interface AiRolePlay extends MinimalAiRolePlay {
  pointsConfiguration?: PointsConfiguration;
  isTodo: boolean;
  expirationDate: string | null;
  hardDeadline: boolean;
  hasCompletedSubmissions: boolean;
  progress: ActivityProgress;
  pointsReceived: number;
  communicationsCount?: number;
  communications: Communication[];
  catalogId: string;
  courseCatalogId: string,
  courseId: number;
  lecturePageId: number;
  lectureComponentId: number;
  description?: string;
  context: string;
  learnerRole: string;
  aiCharacterRole: string;
  aiCharacterName: string;
  aiCharacterPersonality: string;
  aiCharacterFace?: AiCharacterFace;
  aiCharacterObjections: ObjectionType[];
  aiLearnerGoals: ObjectionType[];
  learnerRoleBrief: string;
  situationContextBrief: string;
  goalsBrief: string;
  contentQuality: LectureContentQualityStatus;
  interactionMode: RolePlayInteractionMode;
  voiceOptionKey?: string;
  canStartRolePlay: boolean;
}

export enum Evaluation {
  NEED_WORK = 'needs_work',
  GOOD_JOB = 'good_job',
  EXCELLENT = 'excellent',
}

export type AiRolePlayFeedback = {
  evaluation: Evaluation;
  overallSummary: string;
  whereYouDidWell: string[];
  whereYouCanGrow: string[];
};

export type AiRolePlaySubmission = {
  id: number;
  status: string;
  attemptNumber: number;
  finishedAt: string;
  createdAt: string;
  updatedAt: string;
  openingNarrative: string;
  leaderboardRank: number;
  leaderboardPoints: number;
  priorLeaderboardRank: number;
  feedback: AiRolePlayFeedback | null;
  closingReason?: string;
  mergedPlaybackUrl?: string;
  user?: User;
  aiRolePlay: {
    id: number;
    title?: string;
    progress?: ActivityProgress;
    pointsConfiguration?: {
      id: number;
      activityId: number;
      activityType: string;
      points: number;
      pointsReceived: number;
    },
    isRequiredForCompletion?: boolean;
    expirationDate?: string;
  },
};

export interface AiRolePlayNormalized
  extends Omit<AiRolePlay, 'pointsConfiguration' | 'communications'> {
  pointsConfigurationId?: number;
  vieweeSubmissionId?: number;
  communications: number[];
  learnerStartedSubmission: boolean;
  submissionId?: number;
}

export interface AiRolePlaysNormalized {
  [id: string]: AiRolePlayNormalized;
}

export interface AiRolePlaySubmissionsNormalized {
  [id: string]: AiRolePlaySubmission;
}

export type AiRolePlayAvailableFacePreset = {
  presetKey: string;
  name: string;
  displayUrl: string;
};

export type AiRolePlayAvailableVoiceOption = {
  key: string;
  previewAssetPath: string;
  gender: 'male' | 'female';
};
